import React, {useState, useEffect, useMemo} from 'react';
import {
    Rule,
    ProviderType,
    RuleTarget,
    SlackInstallationStatus,
    GitHubInstallationStatus
} from '../types.ts';
import {Save, Plus, Trash2, X, Loader2, AlertTriangle} from 'lucide-react';
import {useAPI} from '@/contexts/APIContext';
import {
    SlackChannel,
    GitHubRepository,
    GitHubTeam,
    GitHubLabel,
    CreateRuleRequest
} from '@/services/apiService';

interface RuleBuilderProps {
    initialData?: Partial<Rule>;
    organizationId: string;
    onSave: (rule: Rule) => void;
    onCancel: () => void;
    onNavigateToIntegrations?: () => void;
    slackInstallation: SlackInstallationStatus | null;
    githubInstallation: GitHubInstallationStatus | null;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

export const RuleBuilder: React.FC<RuleBuilderProps> = ({
                                                            initialData,
                                                            organizationId,
                                                            onSave,
                                                            onCancel,
                                                            onNavigateToIntegrations,
                                                            slackInstallation,
                                                            githubInstallation
                                                        }) => {
    const {api} = useAPI();
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');

    // Source
    const [sourceProvider, setSourceProvider] = useState<ProviderType>(
        initialData?.source?.provider || ProviderType.GITHUB
    );
    const [repository, setRepository] = useState(initialData?.source?.repository || '');

    // Filters
    const [selectedTeams, setSelectedTeams] = useState<string[]>(initialData?.source?.filters?.teams || []);
    const [selectedLabels, setSelectedLabels] = useState<string[]>(initialData?.source?.filters?.labels || []);

    // Targets
    const [targets, setTargets] = useState<RuleTarget[]>(initialData?.targets || []);

    // Slack channels
    const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
    const [channelsLoading, setChannelsLoading] = useState(false);

    // GitHub repos, teams, and labels
    const [githubRepos, setGithubRepos] = useState<GitHubRepository[]>([]);
    const [githubTeams, setGithubTeams] = useState<GitHubTeam[]>([]);
    const [allLabels, setAllLabels] = useState<GitHubLabel[]>([]);
    const [reposLoading, setReposLoading] = useState(false);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [labelsLoading, setLabelsLoading] = useState(false);

    // Saving state
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name || '');
            setDescription(initialData.description || '');
            setRepository(initialData.source?.repository || '');
            setSelectedTeams(initialData.source?.filters?.teams || []);
            setSelectedLabels(initialData.source?.filters?.labels || []);
            setTargets(initialData.targets || []);
        }
    }, [initialData]);

    // Fetch Slack channels when Slack is connected
    useEffect(() => {
        if (api && organizationId && slackInstallation?.is_connected) {
            setChannelsLoading(true);
            api.getSlackChannels(organizationId)
                .then(channels => {
                    setSlackChannels(channels);
                })
                .catch(err => {
                    console.error('Failed to fetch Slack channels:', err);
                    setSlackChannels([]);
                })
                .finally(() => {
                    setChannelsLoading(false);
                });
        } else {
            setSlackChannels([]);
        }
    }, [api, organizationId, slackInstallation?.is_connected]);

    // Fetch GitHub repositories when provider is GitHub and connected
    useEffect(() => {
        if (api && organizationId && sourceProvider === ProviderType.GITHUB && githubInstallation?.is_connected) {
            setReposLoading(true);
            api.getGitHubRepositories(organizationId)
                .then(repos => {
                    setGithubRepos(repos);
                })
                .catch(err => {
                    console.error('Failed to fetch GitHub repositories:', err);
                    setGithubRepos([]);
                })
                .finally(() => {
                    setReposLoading(false);
                });
        } else if (sourceProvider !== ProviderType.GITHUB || !githubInstallation?.is_connected) {
            // Clear repos if provider changed or not connected
            setGithubRepos([]);
            setRepository('');
        }
    }, [api, organizationId, sourceProvider, githubInstallation?.is_connected]);

    // Fetch GitHub teams when GitHub is connected
    useEffect(() => {
        if (api && organizationId && githubInstallation?.is_connected) {
            setTeamsLoading(true);
            api.getGitHubTeams(organizationId)
                .then(teams => {
                    setGithubTeams(teams);
                })
                .catch(err => {
                    console.error('Failed to fetch GitHub teams:', err);
                    setGithubTeams([]);
                })
                .finally(() => {
                    setTeamsLoading(false);
                });
        } else {
            setGithubTeams([]);
        }
    }, [api, organizationId, githubInstallation?.is_connected]);

    // Fetch all GitHub labels when component mounts
    useEffect(() => {
        if (api && organizationId) {
            setLabelsLoading(true);
            api.getGitHubLabels(organizationId)
                .then(labels => {
                    setAllLabels(labels);
                })
                .catch(err => {
                    console.error('Failed to fetch GitHub labels:', err);
                    setAllLabels([]);
                })
                .finally(() => {
                    setLabelsLoading(false);
                });
        }
    }, [api, organizationId]);

    // Filter labels based on selected repository
    const filteredLabels = useMemo(() => {
        if (!repository) return [];
        return allLabels.filter(label => label.repository_full_name === repository);
    }, [allLabels, repository]);

    // Clear invalid labels when repository changes
    useEffect(() => {
        if (repository && allLabels.length > 0) {
            const validLabelNames = allLabels
                .filter(l => l.repository_full_name === repository)
                .map(l => l.name);
            setSelectedLabels(prev => prev.filter(label => validLabelNames.includes(label)));
        }
    }, [repository, allLabels]);

    const toggleSelection = (item: string, currentList: string[], setter: (list: string[]) => void) => {
        if (currentList.includes(item)) {
            setter(currentList.filter(i => i !== item));
        } else {
            setter([...currentList, item]);
        }
    };

    const addTarget = () => {
        // Only allow adding targets if Slack is connected (only implemented notification provider)
        if (!slackInstallation?.is_connected) {
            return;
        }
        setTargets([...targets, {
            id: generateId(),
            provider: ProviderType.SLACK,
            target: '',
            targetName: ''
        }]);
    };

    const removeTarget = (id: string) => {
        setTargets(targets.filter(t => t.id !== id));
    };

    const updateTarget = (id: string, field: keyof RuleTarget, value: string) => {
        setTargets(targets.map(t => {
            if (t.id === id) {
                // Reset target value if provider changes to ensure valid data type (e.g. text vs select)
                if (field === 'provider') {
                    return {
                        ...t,
                        provider: value as ProviderType,
                        target: '',
                        targetName: ''
                    };
                }
                // When updating target (channel selection), also update targetName
                if (field === 'target') {
                    const channel = slackChannels.find(ch => ch.id === value);
                    return {
                        ...t,
                        target: value,
                        targetName: channel ? `#${channel.name}` : value
                    };
                }
                return {...t, [field]: value};
            }
            return t;
        }));
    };

    const handleSave = async () => {
        if (!api) return alert('API not available');
        if (!name) return alert('Please enter a rule name');
        if (targets.length === 0) return alert('Please add at least one notification target');

        // Validate all targets have a selection
        const emptyTarget = targets.find(t => !t.target);
        if (emptyTarget) return alert('Please select a channel for all notification targets');

        // Validate email targets if any
        const invalidEmail = targets.find(t => t.provider === ProviderType.EMAIL && !t.target.includes('@'));
        if (invalidEmail) return alert('Please enter a valid email address');

        // Find the repository ID from the selected repository
        const selectedRepo = githubRepos.find(r => r.full_name === repository);
        if (!selectedRepo) return alert('Please select a repository');

        setSaving(true);
        setSaveError(null);

        try {
            // Build the API request
            const request: CreateRuleRequest = {
                name,
                description: description || undefined,
                repository_id: selectedRepo.id,
                repository_full_name: selectedRepo.full_name,
                filter_team_slugs: selectedTeams.length > 0 ? selectedTeams : undefined,
                filter_labels: selectedLabels.length > 0 ? selectedLabels : undefined,
                targets: targets.map(t => ({
                    provider: t.provider,
                    target_id: t.target,
                    target_name: t.targetName || t.target,
                })),
            };

            // Call the API
            const response = await api.createRule(organizationId, request);

            // Convert API response to frontend Rule format
            const newRule: Rule = {
                id: response.id,
                organizationId: response.organization_id,
                name: response.name,
                description: response.description || '',
                isActive: response.is_active,
                source: {
                    provider: ProviderType.GITHUB,
                    repository: response.source.repository,
                    filters: {
                        teams: response.source.filters.teams,
                        labels: response.source.filters.labels,
                    },
                },
                targets: response.targets.map(t => ({
                    id: t.id,
                    provider: t.provider as ProviderType,
                    target: t.target,
                    targetName: t.target_name,
                })),
                createdAt: response.created_at,
            };

            onSave(newRule);
        } catch (err) {
            console.error('Failed to save rule:', err);
            setSaveError(err instanceof Error ? err.message : 'Failed to save rule');
            alert('Failed to save rule: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="bg-white rounded-2xl border border-[#e4e0d2] shadow-soft overflow-hidden flex flex-col h-[85vh] md:h-auto max-w-4xl mx-auto">

            {/* Header */}
            <div
                className="border-b border-[#e4e0d2] p-4 md:p-6 bg-[#f4f1e6] flex items-center justify-between sticky top-0 z-10">
                <div>
                    <h2 className="text-xl font-bold text-[#0a0a0a]">
                        {initialData?.id ? 'Edit Rule' : 'New Automation'}
                    </h2>
                    <p className="text-[#6f6b60] text-sm font-medium">Configure your repository
                        triggers</p>
                </div>
                <button onClick={onCancel}
                        className="p-2 hover:bg-[#ece8db] rounded-full transition-colors">
                    <X size={24} className="text-[#6f6b60]"/>
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="space-y-8">

                    {/* Section 1: Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide">Rule
                                Name</label>
                            <input
                                value={name} onChange={e => setName(e.target.value)}
                                className="w-full rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-medium text-[#0a0a0a] placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 transition-all"
                                placeholder="e.g. Backend Review Alerts"
                            />
                        </div>
                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide">Description</label>
                            <input
                                value={description} onChange={e => setDescription(e.target.value)}
                                className="w-full rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-medium text-[#0a0a0a] placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 transition-all"
                                placeholder="Briefly describe what this rule does"
                            />
                        </div>
                    </div>

                    <div className="border-t border-[#e4e0d2]"></div>

                    {/* Section 2: Source */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-[#0a0a0a]">Source Repository</h3>

                        {/* Provider Selection */}
                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide">Provider</label>
                            <div className="relative">
                                <select
                                    value={sourceProvider}
                                    onChange={(e) => setSourceProvider(e.target.value as ProviderType)}
                                    className="w-full appearance-none rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-medium text-[#0a0a0a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 cursor-pointer transition-all"
                                >
                                    <option value={ProviderType.GITHUB}>
                                        GitHub {githubInstallation?.is_connected ? '' : '(Not Connected)'}
                                    </option>
                                    <option value={ProviderType.GITLAB} disabled>
                                        GitLab (Coming Soon)
                                    </option>
                                </select>
                                <div
                                    className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="#6f6b60"
                                              strokeWidth="2" strokeLinecap="round"
                                              strokeLinejoin="round"/>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Repository Selection */}
                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide">Repository</label>
                            <div className="relative">
                                {sourceProvider === ProviderType.GITHUB && !githubInstallation?.is_connected ? (
                                    <div
                                        className="w-full px-3.5 py-2.5 bg-[#fef3c7] border border-[#fde68a] rounded-xl text-sm font-medium text-[#92600a]">
                                        <span>GitHub is not connected. </span>
                                        {onNavigateToIntegrations ? (
                                            <button
                                                onClick={onNavigateToIntegrations}
                                                className="underline hover:text-[#7a5008] font-bold"
                                            >
                                                Go to Integrations
                                            </button>
                                        ) : (
                                            <span>Connect GitHub in Integrations first.</span>
                                        )}
                                    </div>
                                ) : sourceProvider === ProviderType.GITLAB ? (
                                    <div
                                        className="w-full px-3.5 py-2.5 bg-[#ece8db] border border-[#e4e0d2] rounded-xl text-sm font-medium text-[#6f6b60]">
                                        GitLab integration coming soon.
                                    </div>
                                ) : reposLoading ? (
                                    <div
                                        className="w-full px-3.5 py-2.5 bg-[#ece8db] border border-[#e4e0d2] rounded-xl text-sm font-medium text-[#9a948a]">
                                        Loading repositories...
                                    </div>
                                ) : githubRepos.length === 0 ? (
                                    <div
                                        className="w-full px-3.5 py-2.5 bg-[#fef3c7] border border-[#fde68a] rounded-xl text-sm font-medium text-[#92600a]">
                                        No repositories found for this organization.
                                    </div>
                                ) : (
                                    <>
                                        <select
                                            value={repository}
                                            onChange={(e) => setRepository(e.target.value)}
                                            className={`w-full appearance-none rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-mono font-medium focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 cursor-pointer transition-all ${repository ? 'text-[#0a0a0a]' : 'text-[#9a948a]'}`}
                                        >
                                            <option value="" disabled>Select a repository...</option>
                                            {githubRepos.map(repo => (
                                                <option key={repo.id}
                                                        value={repo.full_name}>{repo.full_name}</option>
                                            ))}
                                        </select>
                                        <div
                                            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <svg width="12" height="8" viewBox="0 0 12 8"
                                                 fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 1.5L6 6.5L11 1.5" stroke="#6f6b60"
                                                      strokeWidth="2" strokeLinecap="round"
                                                      strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-[#9a948a] font-medium pt-1">Triggers on Pull
                                Request review events.</p>
                        </div>
                    </div>

                    <div className="border-t border-[#e4e0d2]"></div>

                    {/* Section 3: Filters */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-bold text-[#0a0a0a]">Filters
                            (Optional)</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label
                                    className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide block mb-3">Reviewer
                                    Teams</label>
                                <div className="flex flex-wrap gap-2">
                                    {teamsLoading ? (
                                        <span
                                            className="text-sm text-[#9a948a]">Loading teams...</span>
                                    ) : githubTeams.length === 0 ? (
                                        <span className="text-sm text-[#92600a]">No teams available. Connect GitHub first.</span>
                                    ) : (
                                        githubTeams.map(team => (
                                            <button
                                                key={team.id}
                                                onClick={() => toggleSelection(team.slug, selectedTeams, setSelectedTeams)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all ${
                                                    selectedTeams.includes(team.slug)
                                                        ? 'bg-[#f3e8ff] text-[#7e22ce] border border-transparent ring-1 ring-[#7e22ce]/20'
                                                        : 'bg-[#ece8db] text-[#6f6b60] border border-transparent hover:bg-[#e0dccd]'
                                                }`}
                                            >
                                                @{team.slug}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div>
                                <label
                                    className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide block mb-3">PR
                                    Labels</label>
                                <div className="flex flex-wrap gap-2">
                                    {labelsLoading ? (
                                        <span
                                            className="text-sm text-[#9a948a]">Loading labels...</span>
                                    ) : !repository ? (
                                        <span className="text-sm text-[#6f6b60]">Select a repository first</span>
                                    ) : filteredLabels.length === 0 ? (
                                        <span className="text-sm text-[#6f6b60]">No labels found for this repository</span>
                                    ) : (
                                        filteredLabels.map(label => (
                                            <button
                                                key={label.id}
                                                onClick={() => toggleSelection(label.name, selectedLabels, setSelectedLabels)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all ${
                                                    selectedLabels.includes(label.name)
                                                        ? 'bg-[#fef3c7] text-[#92600a] border border-transparent ring-1 ring-[#92600a]/20'
                                                        : 'bg-[#ece8db] text-[#6f6b60] border border-transparent hover:bg-[#e0dccd]'
                                                }`}
                                                style={{
                                                    borderLeftColor: selectedLabels.includes(label.name) ? undefined : `#${label.color}`,
                                                    borderLeftWidth: selectedLabels.includes(label.name) ? undefined : '4px',
                                                }}
                                            >
                                                {label.name}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-[#e4e0d2]"></div>

                    {/* Section 4: Notifications */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-[#0a0a0a]">Notifications</h3>
                            <button
                                onClick={addTarget}
                                disabled={!slackInstallation?.is_connected}
                                className="flex items-center gap-1 text-sm font-bold text-[#15803d] bg-[#dcfce7] px-3 py-2 rounded-lg hover:bg-[#bbf7d0] transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#dcfce7]"
                            >
                                <Plus size={16} strokeWidth={3}/> Add Target
                            </button>
                        </div>

                        {/* No connected providers warning */}
                        {!slackInstallation?.is_connected && targets.length === 0 && (
                            <div
                                className="text-center p-6 bg-[#fef3c7] rounded-xl border border-[#fde68a]">
                                <p className="text-[#92600a] font-bold mb-1">No notification
                                    providers connected</p>
                                <p className="text-sm text-[#92600a]">
                                    {onNavigateToIntegrations ? (
                                        <>
                                            <button
                                                onClick={onNavigateToIntegrations}
                                                className="underline hover:text-[#7a5008] font-bold"
                                            >
                                                Go to Integrations
                                            </button>
                                            <span> to connect Slack.</span>
                                        </>
                                    ) : (
                                        <span>Connect Slack in Integrations to add notification targets.</span>
                                    )}
                                </p>
                            </div>
                        )}

                        <div className="space-y-3">
                            {targets.map(target => {
                                const isSlack = target.provider === ProviderType.SLACK;
                                const isTeams = target.provider === ProviderType.TEAMS;
                                const isEmail = target.provider === ProviderType.EMAIL;
                                const isChannelBased = isSlack || isTeams;

                                return (
                                    <React.Fragment key={target.id}>
                                    <div className="flex flex-col md:flex-row gap-3 bg-[#faf8f1] p-4 border border-[#e4e0d2] rounded-xl transition-all">
                                        <select
                                            value={target.provider}
                                            onChange={(e) => updateTarget(target.id, 'provider', e.target.value)}
                                            className="bg-white border border-[#d3cdba] rounded-xl px-3.5 py-2.5 font-medium text-sm text-[#0a0a0a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 w-full md:w-48"
                                        >
                                            <option value={ProviderType.SLACK}
                                                    disabled={!slackInstallation?.is_connected}>
                                                Slack {slackInstallation?.is_connected ? '' : '(Not Connected)'}
                                            </option>
                                            <option value={ProviderType.TEAMS} disabled>
                                                Teams (Coming Soon)
                                            </option>
                                            <option value={ProviderType.EMAIL} disabled>
                                                Email (Coming Soon)
                                            </option>
                                        </select>

                                        <div className="flex-1 relative">
                                            {isTeams ? (
                                                <div
                                                    className="w-full px-3.5 py-2.5 bg-[#ece8db] border border-[#e4e0d2] rounded-xl text-sm font-medium text-[#6f6b60]">
                                                    Microsoft Teams integration coming soon.
                                                </div>
                                            ) : isEmail ? (
                                                <div
                                                    className="w-full px-3.5 py-2.5 bg-[#ece8db] border border-[#e4e0d2] rounded-xl text-sm font-medium text-[#6f6b60]">
                                                    Email notifications coming soon.
                                                </div>
                                            ) : isSlack && !slackInstallation?.is_connected ? (
                                                <div
                                                    className="w-full px-3.5 py-2.5 bg-[#fef3c7] border border-[#fde68a] rounded-xl text-sm font-medium text-[#92600a]">
                                                    Slack is not connected. Connect in Integrations
                                                    first.
                                                </div>
                                            ) : isChannelBased ? (
                                                <div className="relative">
                                                    {channelsLoading ? (
                                                        <div
                                                            className="w-full px-3.5 py-2.5 bg-[#ece8db] border border-[#e4e0d2] rounded-xl text-sm font-medium text-[#9a948a]">
                                                            Loading channels...
                                                        </div>
                                                    ) : slackChannels.length === 0 ? (
                                                        <div
                                                            className="w-full px-3.5 py-2.5 bg-[#fef3c7] border border-[#fde68a] rounded-xl text-sm font-medium text-[#92600a]">
                                                            No Slack channels found.
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <select
                                                                value={target.target}
                                                                onChange={(e) => updateTarget(target.id, 'target', e.target.value)}
                                                                className={`w-full appearance-none px-3.5 py-2.5 bg-white border border-[#d3cdba] rounded-xl text-sm font-medium focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 ${target.target ? 'text-[#0a0a0a]' : 'text-[#9a948a]'}`}
                                                            >
                                                                <option value="" disabled>Select a channel...</option>
                                                                {slackChannels.map(ch => (
                                                                    <option key={ch.id}
                                                                            value={ch.id}>#{ch.name}</option>
                                                                ))}
                                                            </select>
                                                            <div
                                                                className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                                                <svg width="10" height="6"
                                                                     viewBox="0 0 12 8" fill="none"
                                                                     xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M1 1.5L6 6.5L11 1.5"
                                                                          stroke="#6f6b60"
                                                                          strokeWidth="2"
                                                                          strokeLinecap="round"
                                                                          strokeLinejoin="round"/>
                                                                </svg>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={target.target}
                                                    onChange={(e) => updateTarget(target.id, 'target', e.target.value)}
                                                    className="w-full px-3.5 py-2.5 bg-white border border-[#d3cdba] rounded-xl text-sm font-medium text-[#0a0a0a] placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30"
                                                    placeholder="Enter email address"
                                                />
                                            )}
                                        </div>

                                        <button
                                            onClick={() => removeTarget(target.id)}
                                            className="p-2.5 text-[#be123c] hover:bg-[#ffe4e6] rounded-xl transition-colors self-end md:self-auto border border-transparent"
                                        >
                                            <Trash2 size={18}/>
                                        </button>
                                    </div>
                                    {/* Warning when app is not in the selected Slack channel */}
                                    {target.provider === 'slack' && target.target && (() => {
                                        const selectedChannel = slackChannels.find(ch => ch.id === target.target);
                                        if (selectedChannel && !selectedChannel.is_member) {
                                            return (
                                                <div className="flex items-start gap-2 px-3 py-2.5 bg-[#fef3c7] border border-[#fde68a] rounded-xl mt-2">
                                                    <AlertTriangle size={16} className="text-[#92600a] shrink-0 mt-0.5" />
                                                    <span className="text-sm text-[#92600a] font-medium">
                                                        ReviewMe is not in this channel. Notifications will fail until you invite the app to #{selectedChannel.name}.
                                                    </span>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    </React.Fragment>
                                );
                            })}

                            {targets.length === 0 && (
                                <div
                                    className="text-center p-8 bg-[#faf8f1] rounded-xl border border-dashed border-[#d3cdba]">
                                    <p className="text-[#6f6b60] font-medium">No notifications
                                        configured.</p>
                                    <p className="text-sm text-[#9a948a]">Add at least one target to
                                        enable this rule.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div
                className="border-t border-[#e4e0d2] p-4 md:p-6 bg-white flex justify-end items-center gap-3">
                <button
                    onClick={onCancel}
                    className="px-6 py-3 rounded-xl border border-[#d3cdba] text-[#4f4f48] font-bold hover:bg-[#f4f1e6] transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={targets.length === 0 || saving}
                    className="px-8 py-3 rounded-xl bg-[#22c55e] text-white font-bold shadow-soft hover:bg-[#16a34a] transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                    {saving ? (
                        <>
                            <Loader2 size={18} strokeWidth={2.5}
                                     className="animate-spin"/> Saving...
                        </>
                    ) : (
                        <>
                            <Save size={18} strokeWidth={2.5}/> Save Automation
                        </>
                    )}
                </button>
            </div>

        </div>
    );
};
